import { Key, MeiliSearch } from 'meilisearch'
import * as Sentry from '@sentry/react'
import { MEILISEARCH_CONFIG } from './api'

export interface SearchResult {
  _id: Key | null | undefined
  _source: any
  announceResult: {
    hits: Array<{
      title: string
      description: string
      phone: string
      email: string
      id: string | number
    }>
    nbHits: number
    processingTimeMs: number
  }
  userResult: {
    hits: Array<{
      lastname: string
      firstname: string
      nickname: string
      status: Array<string>
      id: string | number
      userId: string | number
    }>
    nbHits: number
    processingTimeMs: number
  }
}

class SearchService {
  private client: MeiliSearch

  constructor() {
    this.client = new MeiliSearch({
      host: MEILISEARCH_CONFIG.host,
      apiKey: MEILISEARCH_CONFIG.apiKey,
    })
  }

  async configureIndexes() {
    try {
      await this.client.createIndex('announces', { primaryKey: 'id' })
      await this.client.createIndex('users', { primaryKey: 'userId' })

      await this.client.index('announces').updateSettings({
        searchableAttributes: ['title', 'description'],
        filterableAttributes: ['id'],
        sortableAttributes: ['created_at'],
        displayedAttributes: [
          'id',
          'title',
          'description',
          'phone',
          'email',
          'created_at',
        ],
      })

      await this.client.index('users').updateSettings({
        searchableAttributes: ['nickname', 'firstname', 'lastname'],
        filterableAttributes: ['userId'],
        sortableAttributes: ['created_at'],
        displayedAttributes: [
          'userId',
          'nickname',
          'firstname',
          'lastname',
          'status',
        ],
      })
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          action: 'configureIndexes',
          timestamp: new Date().toISOString(),
        },
        tags: {
          type: 'api_error',
          method: 'configureIndexes',
        },
      })
      throw error
    }
  }

  private isValidAnnounce(hit: any): boolean {
    return !!(hit.title?.trim() || hit.description?.trim())
  }

  private isValidUser(hit: any): boolean {
    return !!(
      hit.nickname?.trim() ||
      hit.firstname?.trim() ||
      hit.lastname?.trim()
    )
  }

  async search(query: string): Promise<SearchResult> {
    try {
      const [announceResult, userResult] = await Promise.all([
        this.client.index('announces').search(query, {
          limit: 10,
          attributesToHighlight: ['title', 'description'],
          attributesToRetrieve: [
            'id',
            'title',
            'description',
            'phone',
            'email',
          ],
        }),
        this.client.index('users').search(query, {
          limit: 10,
          attributesToHighlight: ['nickname', 'firstname', 'lastname'],
          attributesToRetrieve: [
            'userId',
            'nickname',
            'firstname',
            'lastname',
            'status',
          ],
        }),
      ])

      const validAnnounces = announceResult.hits
        .filter(this.isValidAnnounce)
        .map(hit => ({
          id: hit.id,
          title: hit._formatted?.title || hit.title,
          description: hit._formatted?.description || hit.description,
          phone: hit.phone || '',
          email: hit.email || '',
        }))

      const validUsers = userResult.hits.filter(this.isValidUser).map(hit => ({
        id: hit.id,
        userId: hit.userId,
        nickname: hit._formatted?.nickname || hit.nickname,
        firstname: hit._formatted?.firstname || hit.firstname || '',
        lastname: hit._formatted?.lastname || hit.lastname || '',
        status: hit.status || '',
      }))

      return {
        _id: null,
        _source: null,
        announceResult: {
          hits: validAnnounces,
          nbHits: validAnnounces.length,
          processingTimeMs: announceResult.processingTimeMs,
        },
        userResult: {
          hits: validUsers,
          nbHits: validUsers.length,
          processingTimeMs: userResult.processingTimeMs,
        },
      }
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          action: 'search',
          query,
          timestamp: new Date().toISOString(),
        },
        tags: {
          type: 'api_error',
          method: 'search',
        },
      })
      throw error
    }
  }

  async testConnection() {
    try {
      const health = await this.client.health()
      const stats = await this.client.getStats()
      return true
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          action: 'testConnection',
          timestamp: new Date().toISOString(),
        },
        tags: {
          type: 'api_error',
          method: 'testConnection',
        },
      })
      return false
    }
  }
}

export const searchService = new SearchService()
